// src/private/src/pages/app/clients/components/SectionNav.jsx
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Chip, Stack } from '@mui/material';
import { SECTION_NAV_ITEMS } from './dashboardUtils';

/**
 * Horizontal chip bar for quick-jumping to dashboard sections. Rendered inside
 * the sticky controls toolbar; highlights the section currently in view and
 * offsets jump scrolling so targets land below the sticky bar.
 */
function SectionNav({ isStaff = false, scrollOffset = 170 }) {
  const [activeId, setActiveId] = useState('');

  const items = useMemo(
    () => SECTION_NAV_ITEMS.filter((item) => !item.staffOnly || isStaff),
    [isStaff]
  );

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') {
      return undefined;
    }
    const visible = new Map();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            visible.set(entry.target.id, entry.boundingClientRect.top);
          } else {
            visible.delete(entry.target.id);
          }
        });
        if (visible.size) {
          const [topId] = [...visible.entries()].sort((a, b) => a[1] - b[1])[0];
          setActiveId(topId);
        }
      },
      { rootMargin: `-${scrollOffset}px 0px -45% 0px` }
    );
    items.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) {
        observer.observe(el);
      }
    });
    return () => observer.disconnect();
  }, [items, scrollOffset]);

  const handleClick = useCallback(
    (sectionId) => {
      const el = document.getElementById(sectionId);
      if (el) {
        const top = el.getBoundingClientRect().top + window.scrollY - scrollOffset;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    },
    [scrollOffset]
  );

  return (
    <Box
      sx={{
        display: 'flex',
        overflowX: 'auto',
        py: 0.5,
        '&::-webkit-scrollbar': { height: 4 },
        '&::-webkit-scrollbar-thumb': { bgcolor: 'divider', borderRadius: 2 },
      }}
    >
      <Stack direction="row" spacing={0.75} sx={{ minWidth: 'max-content' }}>
        {items.map((item) => (
          <Chip
            key={item.id}
            label={item.label}
            size="small"
            variant={activeId === item.id ? 'filled' : 'outlined'}
            color={activeId === item.id ? 'primary' : 'default'}
            onClick={() => handleClick(item.id)}
            sx={{
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '0.75rem',
              '&:hover': activeId === item.id ? undefined : { bgcolor: 'action.hover' },
            }}
          />
        ))}
      </Stack>
    </Box>
  );
}

export default memo(SectionNav);
